<template>
  <section id="home">
    <!-- Mobile: volle Viewport-Höhe, muted Autoplay -->
    <div v-if="isMobile" class="hero-mobile">
      <div class="hero-mobile__media">
        <video
          ref="mobileVideo"
          class="hero-mobile__video"
          src="/video/trailer_2.mp4"
          poster="/images/trailer_2.webp"
          autoplay
          playsinline
          muted
          loop
          preload="metadata"
        ></video>
      </div>

      <div class="hero-mobile__content whitecolor">
        <h1 class="hero-mobile__title text-captilize">
          <span class="hero-mobile__brand">
            Haus <strong>Birkengrün</strong>
          </span>
          <span class="hero-mobile__subtitle">
            Perfekte Ferien an der <strong>Ostsee</strong>
          </span>
        </h1>
        <div class="hero-mobile__actions">
          <button
            type="button"
            class="button hero-mobile__button hero-mobile__button--primary"
            @click="openBookingModal()"
          >
            Jetzt reservieren
          </button>
          <div class="hero-mobile__secondary-actions">
            <a href="#about_us" class="button white scroll hero-mobile__button">Über Uns</a>
            <a href="#contact_us" class="button light scroll hero-mobile__button">Kontakt</a>
          </div>
        </div>
      </div>
    </div>

    <!-- Desktop: nativer Video-Hero (ohne Revolution Slider) -->
    <div v-else id="maxo-main" class="hero-desktop hero-slider">
      <video
        ref="desktopVideo"
        class="hero-desktop__video"
        src="/video/trailer_2.mp4"
        poster="/images/trailer_2.webp"
        autoplay
        playsinline
        muted
        loop
        preload="metadata"
      ></video>

      <div class="hero-desktop__content whitecolor">
        <h1 class="text-captilize hero-desktop__title animate__animated animate__fadeIn">
          Haus <strong>Birkengrün</strong>
          <span class="blue">
            Perfekte Ferien an der <strong>Ostsee</strong>
          </span>
        </h1>
        <div class="hero-desktop__actions animate__animated animate__fadeInUp">
          <button
            type="button"
            class="button hero-desktop__button--primary"
            @click="openBookingModal()"
          >
            Jetzt reservieren
          </button>
          <a href="#about_us" class="button white scroll">Über Uns</a>
          <a href="#contact_us" class="button light scroll">Kontakt</a>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { onMounted, onBeforeUnmount, ref, watch, nextTick } from 'vue'
import { useBookingModal } from '../../composables/useBookingModal.js'

const MOBILE_MAX_WIDTH = 767

export default {
  setup() {
    const { openBookingModal } = useBookingModal()
    const isMobile = ref(false)
    const mobileVideo = ref(null)
    const desktopVideo = ref(null)
    let mediaQuery = null

    const updateIsMobile = () => {
      if (typeof window === 'undefined') return
      isMobile.value = window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`).matches
    }

    const startVideo = async (video) => {
      if (!video) return

      video.muted = true
      try {
        await video.play()
      } catch (error) {
        console.warn('[Hero] Muted Autoplay nicht möglich:', error)
      }
    }

    const startActiveHeroVideo = () => {
      if (isMobile.value) {
        startVideo(mobileVideo.value)
        return
      }

      startVideo(desktopVideo.value)
    }

    watch(isMobile, () => {
      nextTick(() => startActiveHeroVideo())
    })

    onMounted(() => {
      updateIsMobile()

      mediaQuery = window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`)
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', updateIsMobile)
      } else {
        mediaQuery.addListener(updateIsMobile)
      }

      nextTick(() => startActiveHeroVideo())
    })

    onBeforeUnmount(() => {
      if (!mediaQuery) return
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', updateIsMobile)
      } else {
        mediaQuery.removeListener(updateIsMobile)
      }
    })

    return {
      isMobile,
      mobileVideo,
      desktopVideo,
      openBookingModal
    }
  }
}
</script>

<style scoped lang="scss">
.hero-mobile {
  position: relative;
  width: 100%;
  height: 100svh;
  min-height: 100vh;
  overflow: hidden;
  background: #1a2a35 center / cover no-repeat;
  background-image: url("/images/trailer_2.webp"), url("/images/trailer_2.jpg");

  &__media {
    position: absolute;
    inset: 0;
    z-index: 0;
  }

  &__video {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    display: block;
  }

  &__content {
    position: absolute;
    inset: 0;
    z-index: 2;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 1.5rem 1.25rem 2.5rem;
  }

  &__title {
    font-family: 'Raleway', sans-serif;
    font-weight: 100;
    line-height: 1.2;
    margin: 0 0 1.75rem;
    max-width: 22rem;
    text-shadow: 0 2px 16px rgba(0, 0, 0, 0.55);
  }

  &__brand {
    display: block;
    font-size: 2.35rem;
    margin-bottom: 0.55rem;
    letter-spacing: 0.01em;

    strong {
      font-weight: 500;
    }
  }

  &__subtitle {
    display: block;
    font-size: 1.45rem;
    font-weight: 100;
    line-height: 1.35;
    color: #42d9e4;

    strong {
      font-weight: 500;
      color: #fff;
    }
  }

  &__actions {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: center;
    gap: 0.75rem;
    width: 100%;
    max-width: 21rem;
  }

  &__secondary-actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.65rem;
  }

  &__button {
    flex: 1;
    min-width: 0;
    margin: 0;
    padding: 0.9rem 0.5rem;
    font-size: 0.8125rem;
    letter-spacing: 0.04em;
    text-align: center;
    line-height: 1.2;
    border-radius: 4px;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);
    border: 2px solid transparent;
    cursor: pointer;

    &--primary {
      background: #f2c900;
      border-color: #f2c900;
      color: #515a5f;
      font-weight: bold;
      text-transform: uppercase;
      font-size: 0.9375rem;
      padding: 1rem 0.75rem;
      box-shadow: 0 4px 14px rgba(242, 201, 0, 0.45);
    }

    &.button.light {
      background: #42d9e4;
      border-color: #42d9e4;
      color: #fff;
    }

    &.button.white {
      background: rgba(255, 255, 255, 0.1);
      border: 2px solid rgba(255, 255, 255, 0.9);
      color: #fff;
    }
  }
}

.hero-desktop {
  position: relative;
  width: 100%;
  min-height: 600px;
  height: calc(100vh - 72px);
  overflow: hidden;
  background: #1a2a35 center / cover no-repeat;
  background-image: url("/images/trailer_2.webp"), url("/images/trailer_2.jpg");

  &__video {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    display: block;
  }

  &__content {
    position: absolute;
    inset: 0;
    z-index: 2;
    display: flex;
    flex-direction: column;
    justify-content: center;
    margin-left: 20%;
    padding-right: 2rem;
    pointer-events: none;
  }

  &__title {
    animation-delay: 1s;
    animation-duration: 1.5s;
    animation-fill-mode: both;
    pointer-events: auto;
  }

  &__actions {
    margin-top: 2.5rem;
    animation-delay: 2.5s;
    animation-duration: 1.5s;
    animation-fill-mode: both;
    pointer-events: auto;

    .button {
      margin-right: 8px;
    }

    .hero-desktop__button--primary {
      margin-right: 12px;
      padding: 0.85rem 2rem;
      background: #f2c900;
      border: 2px solid #f2c900;
      color: #515a5f;
      font-weight: bold;
      text-transform: uppercase;
      box-shadow: 0 4px 14px rgba(242, 201, 0, 0.45);
      cursor: pointer;

      &:hover,
      &:focus {
        background: #e0b800;
        border-color: #e0b800;
        color: #515a5f;
      }
    }
  }
}
</style>
